import React, { useEffect, useState } from "react";
import { Modal, Button } from "./ui";
import { LoadingSpinner } from "./LoadingSpinner";
import type { UserLog, Volunteer } from "../data/types";
import { API_BASE_URL, LoadingSpinnerMeta } from "../data";

type UserLogDeailModalProps = {
    log: UserLog | null,
    isOpen: boolean,
    onClose: () => void
}

function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between border-b pb-2">
            <span className="text-gray-500">{label}</span>
            <span className="font-medium">{value}</span>
        </div>
    );
}

function MoveActionDetails({ log }: { log: UserLog }) {
    const detail = log.detail;
    const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchVolunteers = async () => {
            try {
                setLoading(true);

                const params = new URLSearchParams();
                detail.csids.forEach((id: number) => params.append("ids[]", String(id)));

                const response = await fetch(`${API_BASE_URL}/volunteer/by-ids?${params.toString()}`, {
                    credentials: "include",
                    headers: {
                        "Accept": "application/json",
                    }
                });

                if (!response.ok) {
                    throw new Error("Unable to fetch volunteer data");
                }

                const data = await response.json();
                console.log(data);
                setVolunteers(Array.isArray(data) ? data : data.data ?? []);
            } catch (err) {
                console.error("Failed to fetch volunteers", err);
            } finally {
                setLoading(false);
            }
        };

        if (detail.csids?.length > 0) {
            fetchVolunteers();
        }
    }, [detail.csids]);


    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
                <div className="rounded-lg border p-4 bg-gray-50">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                        Source
                    </h3>
                    <div className="space-y-2">
                        <Row label="Event" value={detail.source_event} />
                        <Row label="Area" value={detail.source_area} />
                        <Row label="Shift" value={detail.source_shift} />
                    </div>
                </div>

                <div className="rounded-lg border p-4 bg-green-50">
                    <h3 className="text-sm font-semibold text-green-700 uppercase mb-3">
                        Destination
                    </h3>
                    <div className="space-y-2">
                        <Row label="Event" value={detail.dest_event} />
                        <Row label="Area" value={detail.dest_area} />
                        <Row label="Shift" value={detail.dest_shift} />
                    </div>
                </div>
            </div>

            <div className="rounded-lg border p-4">
                <h3 className="font-semibold mb-3">
                    Volunteers Moved ({detail.csids.length})
                </h3>

                {loading ? (
                    <div className="flex justify-center py-6">
                        <LoadingSpinner meta={LoadingSpinnerMeta} />
                    </div>
                ) : (
                    <div className="space-y-2">
                        {volunteers?.map((volunteer) => (
                            <div
                                key={volunteer.csid}
                                className="rounded-lg border p-4 text-sm space-y-3"
                            >
                                <div className="flex items-center gap-3 pb-3 border-b">
                                    <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                                        {volunteer.first_name[0]}{volunteer.last_name[0]}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-sm">{volunteer.first_name} {volunteer.last_name}</p>
                                        <p className="text-xs text-gray-400">CSID: {volunteer.csid}</p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    {[
                                        { label: "Email", value: volunteer.email },
                                        { label: "Phone", value: volunteer.phone },
                                        { label: "Age", value: volunteer.age ?? "—" },
                                        { label: "Jamat Khana", value: volunteer.jamat_khana },
                                        { label: "Country", value: volunteer.country },
                                    ].map(({ label, value }) => (
                                        <div key={label} className="grid gap-2" style={{ gridTemplateColumns: "100px 1fr" }}>
                                            <span className="text-gray-400">{label}</span>
                                            <span className="font-medium text-gray-700">{String(value)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function AllocateActionDetail({ log }: { log: UserLog }) {
    return <div>Allocate Details</div>;
}

function RemoveActionDetail({ log }: { log: UserLog }) {
    return <div>Remove Details</div>;
}

const resolveAction = (log: UserLog): React.ReactNode => {
    switch (log.action) {
        case "move":
            return <MoveActionDetails log={log} />;
        case "allocate":
            return <AllocateActionDetail log={log} />;
        case "remove":
            return <RemoveActionDetail log={log} />;
        default:
            return null;
    }
};

export const UserLogDetailModal = (props: UserLogDeailModalProps) => {
    const { log, isOpen, onClose } = props;

    if (!log) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`${log.name} Log` || "Log Details"}
            maxWidth="2xl"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        variant="outline"
                        color="neutral"
                    >
                        Close
                    </Button>
                </div>
            }
        >
            <div className="p-4">
                {resolveAction(log)}
            </div>
        </Modal>
    );
};